import {
    useState,
    useEffect,
    useRef,
    ReactNode,
    ChangeEvent,
    SyntheticEvent,
} from 'react';
import styled from 'styled-components';

import Modal from '/components/ui/Modal';
import IconButton from './buttons/IconButton';
import Icon from './Icon';
import LoadingCircle from '/components/ui/LoadingCircle';
import { effects, colors } from '/css';

interface ImageItem {
    url: string;
    metadata?: {
        dimensions: {
            aspectRatio: number;
            width: number;
            height: number;
        };
    };
    [key: string]: any;
}

interface Props {
    onClose: () => void;
    images: ImageItem[];
    openAtIndex?: number;
    actionButton: ReactNode;
}

const percentOf = (x: number, y: number) => {
    const p = Math.floor((x / (y || 1)) * 100);
    return isNaN(p) ? 0 : p;
};

const getImageDimensions = (img: HTMLImageElement) => ({
    naturalWidth: img.naturalWidth,
    naturalHeight: img.naturalHeight,
    width: img.width,
    height: img.height,
});

const percentToNum = (per: number, max: number) =>
    Math.floor((max / 100) * per);

export default function SlideShow({
    onClose,
    images = [],
    openAtIndex,
    actionButton,
}: Props) {
    const [loadingImage, setLoadingImage] = useState<boolean>(true);
    const [viewedImage, setViewedImage] = useState<string>(
        images[openAtIndex || 0]?.url
    );
    const [zoom, setZoom] = useState<number>(0);
    const [minZoom, setMinZoom] = useState<number>(0);

    const imgRef = useRef<HTMLImageElement>(null);
    const imageContainerRef = useRef<HTMLDivElement>(null);

    const imageUrl = (url: string, width?: number) => {
        // if not imgix image we can't request custom size
        if (!url.includes('imgix.net')) return url;

        const query = {
            fm: 'jpg',
            auto: 'format',
            dpr: 2,
            ...(width ? { w: width } : {}),
        } as { [key: string]: any };

        return (
            url.split('?')[0] +
            '?' +
            Object.keys(query)
                .map((k) => k + '=' + query[k])
                .join('&')
        );
    };

    const filename = (url: string) => {
        const img = images.find((img) => img.url === url);
        return img?.filename || '';
    };

    const onImageLoaded = (e: SyntheticEvent<HTMLImageElement>) => {
        const { width, naturalWidth } = getImageDimensions(e.currentTarget);
        const percWidth = percentOf(width, naturalWidth);
        setZoom(percWidth);
        setMinZoom(percWidth);
        setLoadingImage(false);
    };

    useEffect(() => {
        if (!zoom || !imgRef.current || !imageContainerRef.current) return;

        const containerHeight =
            imageContainerRef.current.getBoundingClientRect().height;
        imageContainerRef.current.style.lineHeight = containerHeight - 5 + 'px';

        if (!zoom || zoom === minZoom) {
            imgRef.current.removeAttribute('style');
            return;
        }

        const { naturalWidth, naturalHeight } = getImageDimensions(
            imgRef.current
        );
        const minW = percentToNum(zoom, naturalWidth);
        const minH = percentToNum(zoom, naturalHeight);
        imgRef.current.style.minWidth = minW + 'px';
        imgRef.current.style.minHeight = minH + 'px';
    }, [zoom]);

    const onZoomChange = (e: ChangeEvent<HTMLInputElement>) => {
        const val = Number(e.target.value);
        setZoom(val < minZoom ? minZoom : val);
    };

    const isFirst = () => images[0].url === viewedImage;

    const isLast = () => images[images.length - 1].url === viewedImage;

    const viewedImageINdex = () =>
        images.reduce((acc, img, i) => (viewedImage === img.url ? i : acc), 0);

    const showNext = () => showImage(images[viewedImageINdex() + 1].url);

    const showPrev = () => showImage(images[viewedImageINdex() - 1].url);

    const showImage = (url: string) => {
        setLoadingImage(true);
        setZoom(0);
        setMinZoom(0);
        setViewedImage(url);
    };

    return (
        <Modal
            closeRequestCallback={onClose}
            width={'80vw'}
            style={{ height: '80vh', padding: '16px' }}
            closeIconStyle={{ top: '32px', right: '32px' }}
        >
            <Wrapper>
                <div className="centered mb-12">{filename(viewedImage)}</div>
                <div className="image-container" ref={imageContainerRef}>
                    {loadingImage && (
                        <LoadingCircle
                            className="loading-circle"
                            color={'#000'}
                        />
                    )}
                    <img
                        key={viewedImage}
                        src={imageUrl(viewedImage)}
                        onLoad={onImageLoaded}
                        ref={imgRef}
                    />
                </div>
                <IconButton
                    size="small"
                    variant="tertiary"
                    disabled={isFirst()}
                    onClick={showPrev}
                    icon={<Icon icon="arrow-left" />}
                    className="image-nav prev"
                />
                <IconButton
                    size="small"
                    variant="tertiary"
                    disabled={isLast()}
                    onClick={showNext}
                    icon={<Icon icon="arrow-right" />}
                    className="image-nav next"
                />
                <nav>
                    <ul>
                        {images.map((img) => (
                            <li
                                key={img.url}
                                className={
                                    viewedImage === img.url
                                        ? 'active'
                                        : undefined
                                }
                                onClick={() => showImage(img.url)}
                            >
                                <button
                                    style={{
                                        backgroundImage: `url(${imageUrl(
                                            img.url,
                                            40
                                        )})`,
                                    }}
                                />
                            </li>
                        ))}
                    </ul>
                    {actionButton}
                </nav>
                <IconButton
                    icon={<Icon icon="external-link" />}
                    size="small"
                    variant="tertiary"
                    className="open-oryginal-image"
                    onClick={() => window.open(viewedImage.split('?')[0])}
                />
                {zoom !== null && (
                    <div className="zoom">
                        <input
                            type="range"
                            min={0}
                            max={100}
                            value={zoom}
                            onChange={onZoomChange}
                        />
                        <span>{zoom}%</span>
                    </div>
                )}
            </Wrapper>
        </Modal>
    );
}

const Wrapper = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;

    .image-container {
        height: calc(100% - 36px - 16px);
        margin-bottom: 16px;
        overflow: auto;
        position: relative;
        text-align: center;
        img {
            border-radius: 12px;
            overflow: hidden;
            position: relative;
            z-index: 20;
            max-width: 100%;
            max-height: 100%;
            margin: auto;
            vertical-align: middle;
        }
        .loading-circle {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translateX(-50%) translateY(-50%);
            z-index: 10;
        }
    }

    .image-nav {
        position: absolute;
        top: calc(50% - 36px);
        z-index: 40;
        transform: translateY(-50%);
        background: white !important;
        border-color: white !important;
        &.prev {
            left: 32px;
        }
        &.next {
            right: 32px;
        }
    }

    nav {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        ul {
            display: flex;
            li {
                margin: 0 12px 0 0;
                border-radius: 12px;
                ${effects.shadow};
                width: 36px;
                height: 36px;
                overflow: hidden;
                > button {
                    width: 36px;
                    height: 36px;
                    background: #fff;
                    transition: all 0.2s ease-in-out;
                    background-size: cover;
                    background-position: center center;
                    background-repeat: no-repeat;
                    position: relative;
                }
                &:hover,
                &.active {
                    ${effects.shadowHover};
                    border: 3px solid ${colors.purple};
                    button {
                        top: -3px;
                        left: -3px;
                    }
                }
            }
        }
    }
    .open-oryginal-image {
        position: absolute;
        top: 32px;
        right: 76px;
        background: white !important;
        border-color: white !important;
        width: 36px;
        height: 36px;
        padding: 0px;
        z-index: 30;
    }
    .zoom {
        position: absolute;
        top: 32px;
        left: 32px;
        background: white;
        width: 200px;
        height: 36px;
        z-index: 30;
        border-radius: 12px;
        padding: 0 12px 0;
        display: flex;
        align-items: center;
        input {
            -webkit-appearance: none;
            appearance: none;
            width: 100%;
            height: 6px;
            background: #dddde5;
            border: none;
            outline: none;
            padding: 0;
            border-radius: 3px;
            margin: 0 12px 0 0;

            &::-webkit-slider-thumb {
                -webkit-appearance: none;
                appearance: none;
                width: 20px;
                height: 20px;
                background: ${colors.purple};
                cursor: pointer;
                border-radius: 30px;
            }
            &::-moz-range-thumb {
                width: 20px;
                height: 20px;
                background: ${colors.purple};
                cursor: pointer;
                border-radius: 30px;
            }
        }
        span {
            font-family: 'gellix-semi';
        }
    }
`;
